import {
  GoogleAuthProvider,
  linkWithPopup,
  onAuthStateChanged,
  signInAnonymously,
  signInWithCredential,
  signInWithPopup,
} from "firebase/auth";
import type { Unsubscribe, User } from "firebase/auth";

import { auth } from "../firebase/client";

const EMULATOR_AUTH = import.meta.env.VITE_USE_EMULATORS === "true";

interface EmulatorGoogleIdentity {
  email: string;
  name?: string;
}

function fakeGoogleIdToken(email: string, name?: string): string {
  return JSON.stringify({
    sub: email,
    iss: "",
    aud: "",
    exp: 0,
    iat: 0,
    name: name || email.split("@")[0],
    email,
    email_verified: true,
  });
}

export function getCurrentUser(): User | null {
  return auth.currentUser;
}

export function observeAuth(
  onUser: (user: User | null) => void,
  onError: (error: Error) => void,
): Unsubscribe {
  return onAuthStateChanged(auth, onUser, onError);
}

export async function signInGoogle(): Promise<User> {
  const result = await signInWithPopup(auth, new GoogleAuthProvider());
  return result.user;
}

export async function signInAnonymousUser(): Promise<User> {
  const result = await signInAnonymously(auth);
  return result.user;
}

export async function signInGoogleEmulator({
  email,
  name,
}: EmulatorGoogleIdentity): Promise<User> {
  if (!EMULATOR_AUTH) throw new Error("emulator-only");
  const credential = GoogleAuthProvider.credential(
    fakeGoogleIdToken(email, name),
  );
  const result = await signInWithCredential(auth, credential);
  return result.user;
}

export async function linkGoogleAccount(): Promise<User> {
  if (!auth.currentUser) throw new Error("not-signed-in");
  const result = await linkWithPopup(
    auth.currentUser,
    new GoogleAuthProvider(),
  );
  return result.user;
}

export const emulatorAuthEnabled = EMULATOR_AUTH;
